import { Progress, Row } from "antd";
import { ReactNode, useCallback, useMemo } from "react";
import { getFormattedCurrency } from "../../../../../../lib/currencyFormatter";
import image from "../../../../../../images/account-background.svg";
import { HomeOutlined } from "@ant-design/icons";

type GeneralInfoProps = {
  data: any;
};

export const GeneralInfo = ({ data }: GeneralInfoProps) => {
  const getSecondaryRow = useCallback(
    (label: string, value: number, isLast = false): ReactNode => (
      <Row
        className={`align-center mb-2 secondary-row ${isLast ? "" : "mb-2"}`}
      >
        <p className="my-0 text-sm secondary-row-label mr-1">{label}</p>
        <Row className="secondary-row-value justify-end">
          <p className="my-0 text-md w-500 elipsis">
            {getFormattedCurrency(value)}
          </p>
        </Row>
      </Row>
    ),
    []
  );

  const paidPrincipalBalance: number = useMemo(() => {
    const value: number = data.beginningPrincipal - data.principalBalance;

    return value < 0 ? 0 : value;
  }, [data]);

  const percentage: number = useMemo(
    () => Math.round((paidPrincipalBalance * 100) / data.beginningPrincipal),
    [data, paidPrincipalBalance]
  );

  const getFormattedProgress = useCallback(
    (value: number | undefined) => (
      <div>
        <p className="my-0 text w-500">{value || 0}%</p>
        <p className="my-0 text-sm">Paid</p>
      </div>
    ),
    []
  );

  return (
    <Row className="d-block h-100">
      <Row className="flex-column justify-space-between h-100 top-info position-relative pa-8">
        <img
          src={image}
          alt="account"
          className="position-absolute abstract-back"
        />
        <Row className="align-center w-100">
          <HomeOutlined className="mr-2" />
          <p className="my-0 text-md elipsis address-header">
            {data.propertyAddress || "My Escrow"}
          </p>
        </Row>
        <Row className="align-start justify-space-between">
          <Row className="progress-container">
            <Progress
              type="dashboard"
              percent={percentage}
              strokeColor="#ffffff"
              size={76}
              format={getFormattedProgress}
            />
            <Row className="d-block ml-3 main-stats">
              <h2 className="h h2 w-100 elipsis">
                {getFormattedCurrency(data.principalBalance)}
              </h2>
              <p className="my-0 text-sm w-100 elipsis w-500">
                <span>Paid: </span>
                {getFormattedCurrency(paidPrincipalBalance)}
              </p>
              <p className="my-0 text-sm w-100 elipsis w-500">
                <span>Beginning: </span>
                {getFormattedCurrency(data.beginningPrincipal)}
              </p>
            </Row>
          </Row>
          <Row className="other-info">
            {getSecondaryRow("Impound Balance", data.impoundBalance)}
            {getSecondaryRow("Unpaid Late Charges", data.unpaidLateCharges)}
            {getSecondaryRow("Unpaid Interest", data.unpaidInterest, true)}
          </Row>
        </Row>
      </Row>

      <Row className="other-info mobile">
        {getSecondaryRow("Impound Balance", data.impoundBalance)}
        {getSecondaryRow("Unpaid Late Charges", data.unpaidLateCharges)}
        {getSecondaryRow("Unpaid Interest", data.unpaidInterest, true)}
      </Row>
    </Row>
  );
};
